import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { buildReadyRepackRows } from "@/lib/repackReadyDashboard";

const key = (prefix) => prefix + "-" + Date.now() + "-" + Math.random().toString(36).slice(2, 11);
const positiveInt = (v) => Number.isSafeInteger(Number(v)) && Number(v) > 0;
const errMessage = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message || "Transaksi gagal diproses.";

export default function SkuRebrandingPanel({ isOwner, purchaseSources = [], showDashboard = true }) {
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]);
  const [ready, setReady] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [dashboardSearch, setDashboardSearch] = useState("");
  const [dashboardSourceFilter, setDashboardSourceFilter] = useState("ALL");
  const [pair, setPair] = useState({ from_product_id: "", to_product_id: "" });
  const [convert, setConvert] = useState({ rule_id: "", qty: "", note: "" });
  const [busy, setBusy] = useState("");
  const [convertKey, setConvertKey] = useState(() => key("rebrand"));

  const load = async () => {
    const calls = [api.get("/sku-rebranding/rules"), api.get("/sku-rebranding/ready")];
    if (isOwner) calls.push(api.get("/products"), api.get("/sku-rebranding/transfers"));
    const res = await Promise.all(calls);
    setRules(Array.isArray(res[0].data) ? res[0].data : []);
    setReady(Array.isArray(res[1].data) ? res[1].data : []);
    if (isOwner) {
      setProducts(Array.isArray(res[2].data) ? res[2].data : []);
      setTransfers(Array.isArray(res[3].data) ? res[3].data : []);
    }
    setStatus("ready");
  };
  useEffect(() => {
    load().catch((e) => { setStatus("unavailable"); setError(errMessage(e)); });
    // Independent feature. Its absence must never break Purchase-sourced Repack.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const activeRules = useMemo(() => rules.filter((r) => r.is_active), [rules]);
  const selectedRule = activeRules.find((r) => r.id === convert.rule_id);
  const existingPair = rules.find((r) => r.from_product_id === pair.from_product_id && r.to_product_id === pair.to_product_id);
  const mappingBlockReason = !pair.from_product_id || !pair.to_product_id
    ? "Pilih SKU asal dan tujuan terlebih dahulu."
    : pair.from_product_id === pair.to_product_id
      ? "SKU asal dan tujuan harus berbeda."
      : existingPair
        ? existingPair.is_active ? "Pasangan ini sudah terdaftar." : "Mapping ini nonaktif; perlu diaktifkan kembali melalui pengaturan OWNER."
        : busy ? "Sedang memproses, tunggu sebentar." : "";
  const qtyValid = selectedRule && selectedRule.source_cost_initialized && positiveInt(convert.qty) && Number(convert.qty) <= selectedRule.source_good_qty;
  const dashboardRows = useMemo(() => buildReadyRepackRows(purchaseSources, ready), [purchaseSources, ready]);
  const visibleRows = dashboardRows.filter((row) => {
    if (dashboardSourceFilter !== "ALL" && row.sourceType !== dashboardSourceFilter) return false;
    const q = dashboardSearch.trim().toLowerCase();
    return !q || [row.date, row.source, row.sku, row.note].some((value) => String(value || "").toLowerCase().includes(q));
  });

  const createRule = async () => {
    setError(""); setNotice(""); setBusy("mapping");
    try {
      if (!pair.from_product_id || !pair.to_product_id || pair.from_product_id === pair.to_product_id) throw new Error("Pilih SKU asal dan tujuan yang berbeda.");
      if (existingPair) throw new Error("Pasangan sudah terdaftar; jangan buat duplikat.");
      await api.post("/sku-rebranding/rules", pair);
      setNotice("Mapping konversi 1:1 tersimpan.");
      setPair({ from_product_id: "", to_product_id: "" });
      await load();
    } catch (e) { setError(errMessage(e)); }
    finally { setBusy(""); }
  };

  const postConversion = async () => {
    setError(""); setNotice("");
    if (!qtyValid) return setError("Qty melebihi stok GOOD sumber atau HPP sumber belum valid.");
    if (!window.confirm(`Pindah aset ${selectedRule.from_sku} → ${selectedRule.to_sku} sebanyak ${convert.qty} unit (1:1)? Ini bukan penjualan.`)) return;
    setBusy("convert");
    try {
      const { data } = await api.post("/sku-rebranding/transfers", {
        rule_id: convert.rule_id, qty: Number(convert.qty), note: convert.note || null,
        idempotency_key: convertKey,
      });
      setNotice(`Konversi ${data.transfer_no} tersimpan. Aset berpindah tanpa penjualan; sumber Repack tersedia.`);
      setConvert({ rule_id: "", qty: "", note: "" });
      setConvertKey(key("rebrand"));
      await load();
    } catch (e) { setError(errMessage(e)); }
    finally { setBusy(""); }
  };

  return <section className="panel" style={{ marginBottom: 16 }} data-testid="sku-rebranding-panel">
    <h2 className="panel-title">Konversi SKU 1:1 → Barang Siap Repack</h2>
    <p className="hint" style={{ marginBottom: 14 }}>Konversi memindahkan stok dan nilai aset antar-SKU, tanpa penjualan. Repack mencatat pekerjaan operator, bukan stok masuk kedua kalinya.</p>

    {status === "loading" && <div className="hint">Memuat sumber konversi...</div>}
    {status === "unavailable" && <div className="hint" data-testid="sku-rebranding-unavailable">Modul konversi belum aktif di lingkungan ini. Repack dari Purchase tetap dapat digunakan.</div>}
    {error && <div className="alert-error" role="alert">{error}</div>}
    {notice && <div className="alert-info">{notice}</div>}

    {status === "ready" && <>
      {isOwner && <div style={{ border: "1px solid #dde4e9", borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, marginBottom: 10 }}>Mapping Rebranding (OWNER)</h3>
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
          <select className="field-input" aria-label="SKU asal mapping" value={pair.from_product_id} onChange={(e) => setPair({ ...pair, from_product_id: e.target.value })} style={{ flex: 1, minWidth: 175 }}><option value="">SKU Asal</option>{products.filter((p) => p.status === "ACTIVE").map((p) => <option key={p.id} value={p.id}>{p.sku_code}</option>)}</select>
          <select className="field-input" aria-label="SKU tujuan mapping" value={pair.to_product_id} onChange={(e) => setPair({ ...pair, to_product_id: e.target.value })} style={{ flex: 1, minWidth: 175 }}><option value="">SKU Tujuan</option>{products.filter((p) => p.status === "ACTIVE").map((p) => <option key={p.id} value={p.id}>{p.sku_code}</option>)}</select>
          <button type="button" className="btn-primary inline" data-testid="rebranding-save-mapping"
            style={{ minWidth: 190, minHeight: 40, whiteSpace: "nowrap" }}
            disabled={!!mappingBlockReason} title={mappingBlockReason || "Simpan pasangan SKU yang diizinkan"}
            onClick={createRule}>{busy === "mapping" ? "Menyimpan..." : "Simpan Mapping 1:1"}</button>
        </div>
        {mappingBlockReason && <div className="hint" role="status" data-testid="rebranding-mapping-help" style={{ marginTop: 7 }}>{mappingBlockReason}</div>}
        {!mappingBlockReason && <div className="hint" data-testid="rebranding-mapping-help" style={{ marginTop: 7 }}>Pasangan siap disimpan. Menyimpan mapping belum mengubah stok.</div>}
        <div style={{ marginTop: 15 }}>
          <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Daftar Mapping Rebranding</h4>
          {rules.length === 0 ? <div className="hint" data-testid="rebranding-no-mappings">Belum ada pasangan yang terdaftar.</div> :
            <div className="table-scroll"><table className="data-table" data-testid="rebranding-mapping-table">
              <thead><tr><th>SKU Asal</th><th>SKU Tujuan</th><th>Rasio</th><th>Status</th></tr></thead>
              <tbody>{rules.map((r) => <tr key={r.id}>
                <td>{r.from_sku}</td><td><b>{r.to_sku}</b></td><td>1:1</td>
                <td>{r.is_active ? "Aktif" : "Nonaktif"}</td>
              </tr>)}</tbody>
            </table></div>}
        </div>
        <h3 style={{ fontSize: 15, margin: "16px 0 9px" }}>Konversi Stok</h3>
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
          <select className="field-input" aria-label="Mapping konversi" value={convert.rule_id} onChange={(e) => setConvert({ ...convert, rule_id: e.target.value })} style={{ flex: 2, minWidth: 210 }}><option value="">Pilih pasangan SKU</option>{activeRules.map((r) => <option key={r.id} value={r.id}>{r.from_sku} → {r.to_sku} (GOOD {r.source_good_qty})</option>)}</select>
          <input className="field-input" aria-label="Qty konversi" type="number" step="1" min="1" max={selectedRule?.source_good_qty ?? undefined} placeholder="Qty" value={convert.qty} onChange={(e) => setConvert({ ...convert, qty: e.target.value })} style={{ width: 100 }}/>
          <input className="field-input" aria-label="Catatan konversi" placeholder="Catatan" value={convert.note} onChange={(e) => setConvert({ ...convert, note: e.target.value })} style={{ flex: 1, minWidth: 170 }}/>
          <button className="btn-primary" disabled={!!busy || !qtyValid} onClick={postConversion}>Konversi 1:1</button>
        </div>
        {selectedRule && <div className="hint" style={{ marginTop: 6 }}>Stok asal GOOD: {selectedRule.source_good_qty}. Nilai persediaan mengikuti AVG existing. {selectedRule.source_cost_initialized ? "" : "HPP sumber belum terinisialisasi."}</div>}
      </div>}

      {showDashboard && <div style={{ border: "1px solid #dde4e9", borderRadius: 8, padding: 14 }} data-testid="repack-ready-dashboard">
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Dashboard SKU — Belum Repack</h3>
        <p className="hint" style={{ marginBottom: 12 }}>Hanya daftar SKU yang masih memiliki sisa pekerjaan Repack. Sumber Purchase dan konversi ditampilkan bersama; ini bukan stok gudang.</p>
        <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <input aria-label="Cari sumber atau SKU" className="field-input" style={{ flex: 1, minWidth: 180 }} placeholder="Cari SKU / Purchase / Konversi..." value={dashboardSearch} onChange={(e) => setDashboardSearch(e.target.value)}/>
          <select aria-label="Filter sumber Repack" className="field-input" style={{ minWidth: 145 }} value={dashboardSourceFilter} onChange={(e) => setDashboardSourceFilter(e.target.value)}>
            <option value="ALL">Semua sumber</option><option value="PURCHASE">Purchase</option><option value="CONVERSION">Konversi</option>
          </select>
          <span className="hint" data-testid="repack-ready-total">{visibleRows.length} sumber belum selesai</span>
        </div>
        <div className="table-scroll">
          <table className="data-table" data-testid="repack-ready-table">
            <thead><tr><th>Tanggal Sumber</th><th>Sumber</th><th>SKU</th><th style={{ textAlign: "right" }}>Total</th><th style={{ textAlign: "right" }}>Sisa</th></tr></thead>
            <tbody>
              {visibleRows.length === 0 && <tr><td colSpan={5} className="empty-cell">Tidak ada sumber Repack yang masih memiliki sisa sesuai filter.</td></tr>}
              {visibleRows.map((row) => <tr key={row.key} data-testid={`repack-ready-row-${row.key}`}>
                <td style={{ whiteSpace: "nowrap" }}>{row.date || "-"}</td>
                <td><b>{row.source}</b><div className="hint">{row.sourceType === "CONVERSION" ? "Konversi SKU" : "Purchase"}</div></td>
                <td><b>{row.sku}</b>{row.note && <div className="hint">{row.note}</div>}</td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{row.total}</td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}><b>{row.remaining}</b></td>
              </tr>)}
            </tbody>
          </table>
        </div>
        <p className="hint" style={{ marginTop: 10 }}>Sisa berkurang saat pekerjaan tercatat dan tidak muncul lagi jika 0. Tanggal Purchase berasal dari dokumen Purchase, bukan rincian tanggal setiap Receiving.</p>
        {ready.length > 0 && <p className="hint" style={{ marginTop: 5 }}>Sumber Konversi saat ini untuk pemantauan saja. Input Pekerjaan Harian di bawah masih menggunakan sumber Purchase; integrasi sumber konversi akan dibuat terpisah.</p>}
      </div>}
      {isOwner && transfers.length > 0 && <div className="hint" style={{ marginTop: 9 }}>Total dokumen konversi tercatat: {transfers.length}. Riwayat dipertahankan sebagai audit aset; bukan omset.</div>}
    </>}
  </section>;
}
